import { selectCurrency } from '@/redux/config/configSlice';
import { selectActiveTicket } from '@/redux/data/dataSlice';
import { ItemType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface ItemCardProps {
  item: ItemType;
  onItemPress: (item: ItemType) => void;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

const ItemCard: FC<ItemCardProps> = ({ item, onItemPress, size = 'medium', className = '' }) => {
  const { t, i18n } = useTranslation();
  const currency = useAppSelector(selectCurrency);
  const activeTicket = useAppSelector(selectActiveTicket);
  const lang = i18n.language;
  const isArabic = lang === 'ar';

  const activeItemQuantity = activeTicket?.items?.find((i) => i.ID === item.ID)?.quantity || 0;

  // Determine sizes based on the size prop
  const iconSizes = {
    small: 'w-[20px] h-[20px]',
    medium: 'w-[28px] h-[28px]',
    large: 'w-[36px] h-[36px]',
  };

  const textSizes = {
    small: 'text-[11px]',
    medium: 'text-[13px]',
    large: 'text-[15px]',
  };

  const priceSizes = {
    small: 'text-[10px]',
    medium: 'text-[12px]',
    large: 'text-[14px]',
  };

  const stockSizes = {
    small: 'text-[9px]',
    medium: 'text-[11px]',
    large: 'text-[13px]',
  };

  const containerPadding = {
    small: 'p-1',
    medium: 'p-1.5',
    large: 'p-2',
  };

  return (
    <div
      onClick={() => onItemPress(item)}
      className={`relative flex flex-col items-center bg-white hover:bg-gray-50 gap-0.5 ${containerPadding[size]} 
        border border-gray-200 shadow-sm rounded dark:border-gray-700 cursor-pointer 
        transition-all duration-200 hover:shadow-md ${className} ${!item.enabled ? 'opacity-60' : ''}`}
    >
      {activeItemQuantity > 0 && (
        <div
          className={`absolute ${size === 'small' ? 'top-0.5 right-0.5' : 'top-1 right-1'} 
            ${size === 'small' ? 'min-w-[16px] h-[16px] text-[10px]' : 'min-w-[20px] h-[20px] text-xs'}
            flex items-center justify-center bg-blue-500 text-white font-semibold rounded-full px-1`}
        >
          {activeItemQuantity}
        </div>
      )}
      {item.iconType === 'COLOR' && (
        <div
          className={`${iconSizes[size]} rounded-full`}
          style={{ backgroundColor: item.iconValue }}
        />
      )}
      {item.iconType === 'IMAGE' && (
        <div className={`${iconSizes[size]} rounded-full overflow-hidden`}>
          <img src={item.iconValue} alt={item.name} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex flex-col gap-0 items-center justify-center w-full">
        <span
          className={`${textSizes[size]} font-semibold text-gray-800 dark:text-gray-200 text-center truncate w-full`}
        >
          {isArabic && item.nameAr ? item.nameAr : item.name}
        </span>
        {item.price && (
          <span className={`${priceSizes[size]} font-semibold text-gray-600 dark:text-gray-300`}>
            {(item.priceAfterTax ?? item.price).toFixed(2)} {currency}
          </span>
        )}
        {item.stock !== null && item.trackStock && (
          <span className={`${stockSizes[size]} text-gray-500 dark:text-gray-400`}>
            {item.stock} {t('common:in_stock')}
          </span>
        )}
      </div>
    </div>
  );
};

export default ItemCard;
